/// <reference path="../../core/services/resource.service.ts" />
'use strict';

namespace ekh {

    const ONE_SEVEN = !!TRPCONFIG.Rtl ? '‎7 x 1' : '1 x 7';
    const TWO_SEVEN = !!TRPCONFIG.Rtl ? '‎7 x 2' : '2 x 7';
    const ONE_TWO_THREE = !!TRPCONFIG.Rtl ? '‎23 x 1' : '1 x 23';
    const TWO_TWO_THREE = !!TRPCONFIG.Rtl ? '‎23 x 2' : '2 x 23';
    const TWO_THREE_TWO = !!TRPCONFIG.Rtl ? '‎32 x 2' : '2 x 32';


    export enum TransformedType {
        EconomySpecial,
        EconomySaver,
        EconomyFlex,
        EconomyFlexPlus,
        BusinessSpecial,
        BusinessSaver,
        BusinessFlex,
        BusinessFlexPlus,
        FirstSaver,
        FirstFlex,
        FirstFlexPlus,
        NotDefined
    }

    export enum AttributeTypes {
        Baggage = 0,
        CabinBaggage,
        SeatsSelection,
        Meals,
        WiFi,
        Refundable,
        Changes,
        PriorityCheckin,
        FastTrack
    }

    export interface IBrandedFareType {
        FlightSectors: IFlightSector[];
        BrandedFareTypeTotalPrice: number;
        [key: string]: any;
    }

    export interface IFlightSector {
        [key: string]: any;
        TransformedType: TransformedType;
        BaggageType: string;
        BaggageText: string;
        CabinBaggageText: string;
        SeatsSelectionText: string;
        ChauffeurText: string;
    }

    export class BrandedFareTypesService {

        constructor(
            private resourceService: Core.ResourceService
        ) {
        }

        public transform(brandedFareType: {[key: string]: any}): IBrandedFareType {
            const flightSectors = brandedFareType.FlightSectors.map(fs => {
               fs.TransformedType = this._getTransformedType(fs.BrandTitle);
               fs.BaggageType = this._getBaggageType(fs);
               fs.BaggageText = this.getBaggage(fs);
               fs.CabinBaggageText = this.getCabinBaggage(fs);
               fs.ChauffeurText = this.getChauffeur(fs);
               fs.SeatsSelectionText = this.getSeatsSelection(fs);
               return fs;
            });

            const brandedFareTypePrice = this.getBrandedFareTypePrice(brandedFareType);

            return {
                ...brandedFareType,
                FlightSectors: flightSectors,
                BrandedFareTypeTotalPrice: brandedFareTypePrice
            };
        }

        public squeeze(brandedfareTypes: IBrandedFareType[]): IBrandedFareType[] {
            return brandedfareTypes.reduce((prev: IBrandedFareType[], curBf: IBrandedFareType) => {
                if (prev.length) {
                    const lastBf = prev.slice(-1)[0];
                    const sectorFirstEqual = this._isEqualFlightSectors(
                        lastBf.FlightSectors[0],
                        curBf.FlightSectors[0]
                    );
                    const sectorSecondEqual = this._isEqualFlightSectors(
                        lastBf.FlightSectors[1],
                        curBf.FlightSectors[1]
                    );
                    if (!sectorFirstEqual || !sectorSecondEqual) {
                        prev.push(curBf);
                    }
                } else {
                    prev.push(curBf);
                }
                return prev;
            }, [] as IBrandedFareType[]);
        }

        private _isEqualFlightSectors(oneFs: IFlightSector, secondFs: IFlightSector): boolean {
            const props = [
                'BaggageText',
                'CabinBaggageText',
                'SeatsSelectionText',
                'ChauffeurText'];
            let res = true;
            for (let i = 0; i < props.length; ++i) {
                let propName = props[i];
                if (oneFs[propName] !== secondFs[propName]) {
                    res = false;
                    break;
                }
            }
            return res;
        }

        public getBrandedFareTypePrice(brandedFareType: {[key: string]: any}): number {
            if (brandedFareType.TotalAmount) {
                return brandedFareType.TotalAmount;
            }
            const adultPrice = this._getPricePerPassengerAge('Adult', brandedFareType);
            const childPrice = this._getPricePerPassengerAge('Child', brandedFareType);
            const infantPrice = this._getPricePerPassengerAge('Infant', brandedFareType);
            return adultPrice + childPrice + infantPrice;
        }

        private _getPricePerPassengerAge(type: string, brandedFareType): number {
            const fare = _.get<number>(brandedFareType, `${type}Fare.Amount`) || 0;
            const fareQuantity = _.get<number>(brandedFareType.priceQuantities, `${type}FareQuantity`) || 0;
            const tax = _.get<number>(brandedFareType, `${type}Tax.Amount`) || 0;
            const taxQuantity = _.get<number>(brandedFareType.priceQuantities, `${type}TaxQuantity`) || 0;
            return fare * fareQuantity + tax * taxQuantity;
        }

        public getBaggage(sector: IFlightSector) {
            switch (sector.BaggageType) {
                case 'ONE_TWO_THREE':
                    return `${ONE_TWO_THREE} ${this.resourceService.get('BRANDED_FARE_TYPES_KGPP')}`;
                case 'PIECE_CONCEPT':
                    return this.resourceService.get('BRANDED_FARE_TYPES_BAGGAGE_PIECE_CONCEPT');
                case 'TWO_TWO_THREE':
                    return `${TWO_TWO_THREE} ${this.resourceService.get('BRANDED_FARE_TYPES_KGPP')}`;
                case 'TWO_THREE_TWO':
                    return `${TWO_THREE_TWO} ${this.resourceService.get('BRANDED_FARE_TYPES_KGPP')}`;
                case 'NOT_INCLUDED':
                    return this.resourceService.get('BRANDED_FARE_TYPES_BAGGAGE_NOT_INCLUDED');
                default:
                    return `${sector.BaggageType} ${this.resourceService.get('BRANDED_FARE_TYPES_KGPP')}`;
            }
        }

        private _getBaggageType(sector: IFlightSector) {
            const attr = this._getAttribute(sector, AttributeTypes.Baggage);
            const checkedMatcher = attr.Text.match(/^Checked[^\d]+(\d+)kg/);
            const cabin = this._getCabin(sector);
            const isEconomySaver = sector.TransformedType === TransformedType.EconomySaver;
            const isEconomySpecial = sector.TransformedType === TransformedType.EconomySpecial;

            if (attr.AttributeInclusionType === 0) {
                switch (true) {
                    case checkedMatcher !== null:
                        return checkedMatcher[1];
                    case cabin === 1 && isEconomySpecial:
                        return 'ONE_TWO_THREE';
                    case cabin === 1 && isEconomySaver:
                        return 'PIECE_CONCEPT';
                    case cabin === 1:
                        return 'TWO_TWO_THREE';
                    default:
                        return 'TWO_THREE_TWO';
                }
            } else {
                return 'NOT_INCLUDED';
            }
        }

        public getCabinBaggage(sector: IFlightSector) {
            const cabin = this._getCabin(sector);
            const attr = this._getAttribute(sector, AttributeTypes.CabinBaggage);
            return attr.AttributeInclusionType === 0 && cabin === 1 ?
                `${ONE_SEVEN} ${this.resourceService.get('BRANDED_FARE_TYPES_KGPP')}` :
                `${TWO_SEVEN} ${this.resourceService.get('BRANDED_FARE_TYPES_KGPP')}`;
        }

        public getSeatsSelection(sector: IFlightSector) {
            if (this.isRestrictedSeats(sector)) {
                return this.resourceService.get('BRANDED_FARE_TYPES_SEAT_SELECTION_RESTRICTED');
            }
            const attr = this._getAttribute(sector, AttributeTypes.SeatsSelection);
            if (attr.AttributeInclusionType === 0) {
                return this.resourceService.get('BRANDED_FARE_TYPES_SEAT_SELECTION_COMPLIMENTARY');
            } else {
                return this.resourceService.get('BRANDED_FARE_TYPES_SEAT_SELECTION_AT_CHECK_IN');
            }
        }

        public isRestrictedSeats(sector: IFlightSector):boolean {
            const attr = this._getAttribute(sector, AttributeTypes.SeatsSelection);
            return attr.AttributeInclusionType === 0 && sector.TransformedType === TransformedType.BusinessSpecial;
        }

        public getChauffeur(sector: IFlightSector) {
            const cabin = this._getCabin(sector);
            return sector.TransformedType === TransformedType.BusinessSpecial || cabin === 1 ?
                this.resourceService.get('BRANDED_FARE_TYPES_CHAUFFEUR_NOT_ELIGIBLE') :
                this.resourceService.get('BRANDED_FARE_TYPES_CHAUFFEUR_ELIGIBLE');
        }

        public containPieceBaggageConcept(fareTypes: IBrandedFareType[], sectorNumber: number): boolean {
            let res = false;
            for (let i = 0; i < fareTypes.length; ++i) {
                const sector = fareTypes[i].FlightSectors[sectorNumber];
                if (sector.BaggageType === 'PIECE_CONCEPT') {
                    res = true;
                    break;
                }
            }
            return res;
        }

        private _getCabin(sector: IFlightSector) {
            return sector.Segments[0].Cabin;
        }

        private _getAttribute(leg, attrType: AttributeTypes): {[key: string]: any} {
            return _.find(leg.BrandAttributes, {AttributeType: attrType});
        }

        private _getTransformedType(brandTitle: string): TransformedType {
            switch (brandTitle.trim().toLowerCase()) {
                case 'economy special':
                    return TransformedType.EconomySpecial;
                case 'economy saver':
                    return TransformedType.EconomySaver;
                case 'economy flex':
                    return TransformedType.EconomyFlex;
                case 'economy flex plus':
                    return TransformedType.EconomyFlexPlus;
                case 'business special':
                    return TransformedType.BusinessSpecial;
                case 'business saver':
                    return TransformedType.BusinessSaver;
                case 'business flex':
                    return TransformedType.BusinessFlex;
                case 'business flex plus':
                    return TransformedType.BusinessFlexPlus;
                case 'first saver':
                    return TransformedType.FirstSaver;
                case 'first flex':
                    return TransformedType.FirstFlex;
                case 'first flex plus':
                    return TransformedType.FirstFlexPlus;
                default:
                    return TransformedType.NotDefined;
            }
        }

    }

    angular
        .module('ekh')
        .service('brandedFareTypesService', BrandedFareTypesService);
}
